import {BrightnessContrast, Gamma, Hsv, isVec2, MixRgb, RgbCurve} from "@cm/material-nodes"
import type {MaterialNode} from "@cm/material-nodes"
import {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import {mixImageWithConstant} from "#material-maps-exporter/core/mix-modes"

type ImageTransform<T extends MaterialNode> = {
    readonly nodeClass: new (...args: never[]) => T
    readonly apply: (node: MaterialNode, input: Nodes.ImageNode) => Nodes.ImageNode
    readonly chainInputs: readonly string[]
}

/**
 * Pairs a node class with its pixel operation, so neither can be listed without the other.
 * `chainInputs` are the inputs the chain may continue through (W-6), of which exactly one is connected.
 */
const imageTransform = <T extends MaterialNode>(
    nodeClass: new (...args: never[]) => T,
    apply: (node: T, input: Nodes.ImageNode) => Nodes.ImageNode,
    chainInputs: readonly string[] = ["color"],
): ImageTransform<T> => ({nodeClass, apply: (node, input) => apply(node as T, input), chainInputs})

const applyRgbCurve = (node: RgbCurve, input: Nodes.ImageNode): Nodes.ToneMap => {
    if (node.parameters === undefined) throw Error(`RGBCurve node has no parameters`)
    const {fac, ...curveParameters} = node.parameters.settings
    if (typeof fac !== "number") throw Error(`RGBCurve node has no valid Fac parameter`)

    const toneMappingParameters = Object.fromEntries<[number, number]>(
        Object.entries(RgbCurve.settingsFromControlPoints(RgbCurve.controlPointsFromSettings(curveParameters))).map(([key, value]) => {
            if (!isVec2(value)) throw Error(`Invalid curve parameter value for key ${key}: ${value}`)
            return [key, [value.x, value.y]]
        }),
    )

    return {type: "toneMap", input, mode: "rgbCurve", parameters: {fac: fac, ...toneMappingParameters}}
}

const applyHsv = (node: Hsv, input: Nodes.ImageNode): Nodes.ToneMap => {
    if (node.parameters === undefined) throw Error(`HueSaturation node has no parameters`)
    const {fac, hue, saturation, value} = node.parameters.settings
    if (typeof fac !== "number") throw Error(`HueSaturation node has no valid Fac parameter`)
    if (typeof hue !== "number") throw Error(`HueSaturation node has no valid Hue parameter`)
    if (typeof saturation !== "number") throw Error(`HueSaturation node has no valid Saturation parameter`)
    if (typeof value !== "number") throw Error(`HueSaturation node has no valid Value parameter`)

    return {type: "toneMap", input, mode: "hueSaturation", parameters: {fac, hue, saturation, value}}
}

const applyGamma = (node: Gamma, input: Nodes.ImageNode): Nodes.Math => {
    if (node.parameters === undefined) throw Error(`Gamma node has no parameters`)
    const {gamma} = node.parameters.settings
    if (typeof gamma !== "number") throw Error(`Gamma node has no valid Gamma parameter`)

    // A math operation takes float32 only, and a decoded png, jpeg or tiff is uint8. The cast has to
    // name a colour space, and linear is the one the renderer applies the node in.
    return {type: "math", firstInput: Nodes.convert(input, "float32", "RGBA", false), secondInput: gamma, operation: "pow"}
}

// The same cast as Gamma's, but to three channels: the multiply and the add would otherwise move
// alpha off 1 as well, where a power leaves it alone.
const applyBrightnessContrast = (node: BrightnessContrast, input: Nodes.ImageNode): Nodes.Math => {
    const {bright = 0, contrast = 0} = node.parameters.settings
    const scaled = Nodes.math("*", Nodes.convert(input, "float32", "RGB", false), 1 + contrast)
    return Nodes.math("max", Nodes.math("+", scaled, bright - contrast / 2), 0)
}

const MIX_RGB_CHAIN_INPUTS = ["color1", "color2"] as const

/**
 * Blender's legacy mix node with one colour wired and the other set on the node. Which of the two is
 * wired is the node's own business, so it is read here rather than carried from the walk.
 */
const applyMixRgb = (node: MixRgb, input: Nodes.ImageNode): Nodes.ImageNode => {
    const {inputs, settings} = node.parameters
    const connected = MIX_RGB_CHAIN_INPUTS.filter((socket) => inputs?.[socket] !== undefined)
    if (connected.length !== 1) throw Error(`RGB Mix needs exactly one of its colour inputs connected, got ${connected.length}`)
    const imageSide = connected[0]
    const constant = imageSide === "color1" ? settings.color2 : settings.color1

    // Three channels for the same reason as Bright/Contrast: the arithmetic must not reach alpha.
    const image = Nodes.convert(input, "float32", "RGB", false)
    // The legacy node clamps its factor before any mode sees it (`svm_mix_clamped_factor`).
    const factor = Math.min(Math.max(settings.fac ?? 0.5, 0), 1)
    const mixed = mixImageWithConstant(settings.blendType ?? "MIX", image, [constant?.r ?? 0, constant?.g ?? 0, constant?.b ?? 0], imageSide, factor)
    return settings.useClamp ? Nodes.math("clip01", mixed) : mixed
}

/** The transforms a chain may contain (V-1), each with the pixel operation it becomes (P-4). */
export const SUPPORTED_IMAGE_TRANSFORMS = [
    imageTransform(RgbCurve, applyRgbCurve),
    imageTransform(Hsv, applyHsv),
    imageTransform(Gamma, applyGamma),
    imageTransform(BrightnessContrast, applyBrightnessContrast),
    imageTransform(MixRgb, applyMixRgb, MIX_RGB_CHAIN_INPUTS),
] as const

type TransformNodeOf<E> = E extends ImageTransform<infer T> ? T : never
export type SupportedImageTransformNode = TransformNodeOf<(typeof SUPPORTED_IMAGE_TRANSFORMS)[number]>

export const isSupportedImageTransformNode = (node: MaterialNode): node is SupportedImageTransformNode =>
    SUPPORTED_IMAGE_TRANSFORMS.some(({nodeClass}) => node instanceof nodeClass)

const transformFor = (node: MaterialNode) => SUPPORTED_IMAGE_TRANSFORMS.find(({nodeClass}) => node instanceof nodeClass)

/** The inputs a transform's chain may continue through, or `undefined` for a node that is not a transform. */
export const transformChainInputs = (node: MaterialNode): readonly string[] | undefined => transformFor(node)?.chainInputs

export const applyImageTransform = (node: SupportedImageTransformNode, input: Nodes.ImageNode): Nodes.ImageNode => {
    const transform = transformFor(node)
    if (transform === undefined) throw Error(`Unsupported image transformation: ${node.nodeClass}`)
    return transform.apply(node, input)
}
